import {Application} from '../application/application';
import {UTM} from '../map/projection';
import * as CryptoJS from 'crypto-js';
import {HexagonGrid} from '../geometry/grid';
import {XHRDataSource} from '../data-source/xhr-source';
import {Datum, SpatialCoordinate, TemporalCoordinate} from '../data-source/types';
import {VisualizationUpdateLevel, VISUALIZER_KEY_MAP, VISUALIZER_KEY_TIMELINE} from '../data-source/visualizer';
import {ObservationTimelineVisualizer} from './obs-tl-visualizer';
import {ObservationMapVisualizer} from './obs-mp-visualizer';
import {ColumnDefinition as TabulatorColumnDefinition} from 'tabulator-tables';
import {Method, ResponseType, xhrPromise, XHRResult} from '../xhr/xhr';
import {DataSet, EncodedSensorData, Signal} from '@mapp/r-mapp-lib';
import {CursorMillis, TimelineRange} from "../timeline/timeline-common";
import {DataList} from "../data-source/data-list";
import {ObservationTypeDefinition} from "./container-observation";

let observationDataSource: ObservationDataSource = undefined;

export const initializer: { [key: string]: (application: Application) => void } = {
    "observation": (application: Application) => {
        console.log('Initializing observation...');
        observationDataSource = new ObservationDataSource(application);
    }
}

export const authenticator: { [key: string]: (application: Application) => void } = {}


export const getDataSource = (): ObservationDataSource => observationDataSource;

/**
 * A minimalistic interface for observational data
 */
export interface ObservationDatum extends Datum {
    geometry: { lat: number, lng: number, xy?: number[], cellIndex?: number[] },
    type: string,
    // ensure this never collides with an actual probe
    weatherData?: Record<string, number>;
}


export interface DeprecatedColumnDefinition {
    key: string,
    title: string,
    schemaType: string
    probeType: string,
    probeOptions?: { table?: any, sortOnLabel?: boolean, dateFormat?: string },
    renderOptions: { sortOnValue?: boolean, depMap?: any, valueToDependentKeyMap?: any }
    datetimeFormat?: string,
    renderType?: string,
    enumLabels?: object,
    dependentColumn?: string | boolean,
    visible: boolean,
    defaultContent: string
}

export interface ColumnDefinition {
    key: string,
    field: string,
    title: string,
    schemaType: string
    probeType: string,
    probeOptions?: { table?: TableProbeOptions, dateFormat?: string, durationPrecision?: number },
    enumLabels?: object,
    dependentColumn: boolean,
    visible: boolean,
    defaultContent: string
    export: boolean,
    exportDateFormat?: string
}

export interface DataTableColumnDefinition extends ColumnDefinition {
    render?: ((data: any, type: string, row: any, meta: object) => any)
}

interface TableProbeOptions {
    valueToDependentKeyMap?: object,
    render: 'auto' | 'mapDependent',
    trueLabel?: string,
    falseLabel?: string,
    sortOnLabel: boolean,
    mappers?: { [key: string]: DatumPropertyDefinition }
}

export interface DatumPropertyDefinition {
    column: ColumnDefinition,
    type: string,
    value: (datum: Datum) => any,
    display: (value: any) => string,
    export: (datum: Datum) => any,
    tabulator: TabulatorColumnDefinition
}

export class ObservationDataSource extends XHRDataSource {
    private timelineVisualizer: ObservationTimelineVisualizer;
    private mapVisualizer: ObservationMapVisualizer;

    public constructor(application: Application) {
        super({
            name: 'observation',
            url: `/${encodeURI(application.getDomain())}/observation/selection/observation`,
            notTemporal: false, // the default
            abortDataRequest: true // the default
        }, application);

        this.timelineVisualizer = <ObservationTimelineVisualizer>this.addVisualizer(new ObservationTimelineVisualizer(VISUALIZER_KEY_TIMELINE, this, 'tlmd-observation'));
        this.mapVisualizer = <ObservationMapVisualizer>this.addVisualizer(new ObservationMapVisualizer(VISUALIZER_KEY_MAP, this, 'tlmd-observation', new HexagonGrid(50.0, true)));
    }

    public removeType(typeId: string): void {
        super.removeType(typeId);
        this.timelineVisualizer.refresh(VisualizationUpdateLevel.data);
        this.mapVisualizer.refresh(VisualizationUpdateLevel.data);
    }

    // Observation's' implementation of DatumReader which should be quicker than the the default implementation 
    public compareByTime = (d0: TemporalCoordinate, d1: TemporalCoordinate): number =>
        d0 == undefined && d1 == undefined ? 0 : d0 == undefined ? 1 : d1 == undefined ? -1 : d0.time - d1.time;
    public temporalHash = (datum: Datum) => CryptoJS.MD5(`${datum.id}_${datum.time}`).toString(CryptoJS.enc.Hex);
    public compareByLocation = (d0: SpatialCoordinate, d1: SpatialCoordinate): number =>
        d0 == undefined && d1 == undefined ? 0 : d0 == undefined ? 1 : d1 == undefined ? -1 :
            d0.lat - d1.lat !== 0 ? d0.lat - d1.lat : d0.lng - d1.lng;
    public spatialHash = (datum: Datum) => CryptoJS.MD5(`${datum.id}_${datum.lat}_${datum.lng}`).toString(CryptoJS.enc.Hex);

    public getVersion = (datum: any) => datum.version !== undefined ? datum.version : datum.poster.time;
    public getTime = (datum: any) => datum.time;
    public getStart = (datum: any) => datum.time;
    public getEnd = (datum: any) => datum.time;
    public getLat = (datum: any) => datum.geometry.lat;
    public getLng = (datum: any) => datum.geometry.lng;
    public getNorth = (datum: any) => datum.geometry.lat;
    public getEast = (datum: any) => datum.geometry.lng;
    public getSouth = (datum: any) => datum.geometry.lat;
    public getWest = (datum: any) => datum.geometry.lng;
    public isTemporal = (_datum: any) => true
    public isMoment = (_datum: any) => true;
    public isRange = (_datum: any) => false;
    public isPerpetual = (_datum: any) => false;
    public isSince = (_datum: any) => false;
    public isUntil = (_datum: any) => false;
    public hasStart = (_datum: any) => false;
    public hasEnd = (_datum: any) => false;
    public getDuration = (_datum: any) => 0;
    public isSpatial = (_datum: any) => true;
    public isPoint = (_datum: any) => true;
    public isArea = (_datum: any) => false;
    public isVersional = (_datum: any) => true;

    protected handleData(timeRange: TimelineRange, animationTiming: CursorMillis[], types: string[], data: any) {
        // do this first
        super.handleData(timeRange, animationTiming, types, data);

        // if the type specifies a feed, we assume we need to grab sensor data
        const typesWithWeather = this.getTypes()
            .filter(type => (this.getDefinitions()[type] as ObservationTypeDefinition)?.features?.weatherFeed !== undefined);

        for (const observationType of typesWithWeather) {
            this.enrichWithSensorFeedData(this.getDataList(observationType),
                (this.getDefinitions()[observationType] as ObservationTypeDefinition).features.weatherFeed);
        }

    }

    /**
     * Gets called just before the data is stored in source's storage
     * Override if some data processing is required
     */
    protected processResponse(response: XHRResult): any {

        let data = <ObservationDatum[]>response.data;

        if (data && data.length > 0) {
            //N.B.!! we make the assumption that all observation coming from an atlas lie in the same UTM zone
            let datum = data[0];
            let utm: UTM = UTM.get(datum.geometry.lat, datum.geometry.lng);

            //calculate the utm coordinates for every observation
            //determine to which grid cell the observation belongs
            for (let i = 0; i < data.length; i++) {
                data[i].geometry.xy = utm.toCartesian(data[i].geometry.lat, data[i].geometry.lng);
                data[i].geometry.cellIndex = this.mapVisualizer.getGrid().getCellIndex(data[i].geometry.xy);
            }
        }

        return data;
    }


    /**
     * enrichWithSensorFeedData expands existing observation items of a single type with (usually weather) data from
     * a given sensor feed. The item list is bucketed/partitioned into buckets of a certain length (currently 1 day).
     * the received data for each bucket is used to interpolate approximate values for each item in the bucket.
     * @param data data list of observation items of a certain type
     * @param feed sensor feed uuid to request data from
     * @private
     */
    private enrichWithSensorFeedData(data: DataList, feed: string): ObservationDatum[] {

        const observations = data.getData() as ObservationDatum[];

        if (observations.length <= 0) {
            return observations;
        }

        observations.sort((itemA, itemB) => itemA.time - itemB.time);

        // bucket observations into same day intervals, might want to toy with this and try weekly buckets
        // or any other interval that might be more beneficial
        // without any additional information we cannot assume anything about the list coming
        // in so we "re-bucket" all the time
        const bucketLength = 24 * 60 * 60 * 1000; // 24 hours

        // the first item starts out in the first bucket
        // the bucket hold indices into the data list
        let buckets = [[0]];
        let dataListIndex = 1; // index into observation list
        let bucketIndex = 0; // current bucket

		//console.log(`Observation at ${new Date(observations[0].time).toISOString()}`);
        while (dataListIndex < observations.length) {
			//console.log(`Observation at ${new Date(observations[dataListIndex].time).toISOString()}`);
            let bucketStartIndex = buckets[bucketIndex][0];
            let tStart = observations[bucketStartIndex].time;
            // fits into current bucket
            if ((observations[dataListIndex].time - tStart) < bucketLength) {
                buckets[bucketIndex].push(dataListIndex);
            } else { // new bucket
                buckets.push([dataListIndex]);
                bucketIndex++;
            }
            dataListIndex++;
        }

        // collect all requests in order to refresh visualizers once we're done
        const serverRequests: Promise<any>[] = [];

        // query weather for each bucket time range
        for (let bucket of buckets) {
            const bucketStart = bucket[0];
            const bucketStartDateTime = new Date(observations[bucketStart].time).toISOString();
            const bucketEndDateTime = new Date(observations[bucketStart].time + bucketLength).toISOString();

            const allBucketItems = bucket.map(index => observations[index]);

            // if all items within the bucket already have weather data, skip this step
            if (!allBucketItems.every(item => !!item.weatherData)) {

                const url = `/${encodeURI(this.getApplication().getDomain())}/sensor/api/feeds/data?rc=1&s0=${bucketStartDateTime}&e0=${bucketEndDateTime}`;
                serverRequests.push(xhrPromise({
                    url: url,
                    method: Method.post,
                    digester: this.getApplication().getDigester(),
                    responseType: ResponseType.json,
                    accept: 'application/json, text/javascript',
                    data: {
                        entity: feed,
                        mimeType: 'application/x-www-form-urlencoded;charset=UTF-8'
                    }
                }).then(response => {
                    const r = response.data as EncodedSensorData;
                    // we assume our data is supplied from one set only
                    const setName = r.feedIdDataSetMap[feed][0];
                    const probeDataSetMap = r.feedIdProbeDataSetMap[feed];
                    const dataSetDef = r.dataSets[setName];
console.log(`soi bucket from ${bucketStartDateTime} to ${bucketEndDateTime}: response=`,r);
                    if (dataSetDef && dataSetDef.data && dataSetDef.data.length > 0) {

                        // this step could be cached probably, since the data set is constant
                        // and the signal does not depend on any value
                        const dataSet = DataSet.get(dataSetDef);
                        const signalsForProbes: Record<string, Signal>
                                    = Object.keys(probeDataSetMap).reduce((previousValue,feedProbeId) => {
                            previousValue[feedProbeId] = dataSet.signal(dataSet.probeIndex(feedProbeId));
                            return previousValue;

                        }, {} as Record<string, Signal>);

                        // move weather into the observation items. note that this works on the actual references
                        // we're not modifying a copy, these changes will pop up eventually in all other viewers
                        // or visualizers

                        for (let item of allBucketItems) {
                            item.weatherData =
                                Object.keys(signalsForProbes).reduce((previousValue, probeKey) => {
                                    previousValue[probeKey] = signalsForProbes[probeKey].get(item.time);
                                    return previousValue;
                                }, {} as Record<string, number>);

                        }

                    }

                })
                .catch(error => console.log(error)));

            } // end-if bucket is missing weather
        }

        Promise.all(serverRequests).then(_ => {
            // refresh all visualizers
            for (let visualizerKey of this.getVisualizorKeys()) {
                this.getVisualizer(visualizerKey).refresh(VisualizationUpdateLevel.data);
            }
        });

    }

}


